// Sidebar menu definition for the Inspectmite shell.
//
// PERMISSIONS: visibility here reproduces the old hardcoded sidebar exactly. The inputs are the
// same two facts it used — "is this user an inspector" and "is this user an administrator"
// (from localStorage user.user_level) — and every link keeps the condition it had before:
//
//   not inspector   Inspections, Clients/Agencies, Settings, Reports
//   administrator   Calendar, Agent Activity report, all of Settings
//   inspector       My Schedule, My Inspections, Client Search, My Reports
//   everyone        Dashboard
//
// Settings links for staff follow twin-ci's staff menu, which checks the level's permission labels
// (ctx.permissions, from api/user-access); see buildSettingsSections.
//
// Only the grouping changed: Management was split into Clients (clients + agencies) and
// Settings (user management + Configuration), to match the new design.

export interface NavLink {
    id: string;          // stable id, used to store Quick Link pins
    label: string;
    icon: string;
    route?: string;      // Angular route (routerLink)
    href?: string;       // full URL, used for pages still served by twin-ci
    description?: string; // one line under the title (Report Library cards, Settings pages)
    planned?: boolean;    // Settings page whose design is not built yet (blank page)
    scope?: string;       // Settings: 'personal' for My Account pages; otherwise organization
}

export interface NavSection {
    title?: string;
    links: NavLink[];
    landing?: string;          // id of the link that is this section's own page (breadcrumbs)
}

export interface NavEntry {
    id: string;
    label: string;
    icon: string;
    route?: string;
    href?: string;
    flyoutTitle?: string;
    landing?: string;          // id of the link that is this group's own page (breadcrumbs)
    sections?: NavSection[];   // present => the entry opens a flyout
}

export interface NavContext {
    isInspector: boolean;
    isAdministrator: boolean;
    inspectionTabOrder: any;   // settings.inspection_tab_order; undefined until settings load
    apiSite: string;           // environment.apiSite — url/<slug> goes through Laravel's redirector
    oldSite: string;           // environment.oldSite — direct twin-ci links
    permissions?: string[];    // staff permission labels (api/user-access); empty until loaded
}

export function buildMenu(ctx: NavContext): NavEntry[] {
    const ci = (slug: string) => ctx.apiSite + 'url/' + slug;
    const menu: NavEntry[] = [];

    menu.push({ id: 'dashboard', label: 'Dashboard', icon: 'dashboard', route: '/dashboard' });

    if (ctx.isInspector) {
        menu.push({ id: 'my-schedule', label: 'My Schedule', icon: 'calendar', route: '/calendar' });
        menu.push({ id: 'my-inspections', label: 'My Inspections', icon: 'clipboard', route: '/inspections-list' });
        menu.push({ id: 'client-search', label: 'Client Search', icon: 'users', route: '/clients/list' });
        menu.push({
            id: 'my-account', label: 'My Reports', icon: 'reports', flyoutTitle: 'My Reports',   // id kept: pinned Quick Links
            sections: [{
                links: [
                    { id: 'jobs-with-bids-mine', label: 'Unscheduled Jobs with Bids', icon: 'file', route: '/report-job-with-bids-non-scheduled' },
                    { id: 'agent-activity-mine', label: 'Agent Activity', icon: 'agent', route: '/agent-report-sales' }
                ]
            }]
        });
        return menu;
    }

    // Not an inspector: administrators and staff.
    const inspectionLinks: NavLink[] = [];
    // The create link waits for settings, exactly like the old *ngIf on inspection_tab_order.
    if (ctx.inspectionTabOrder == 1) {
        inspectionLinks.push({ id: 'create-inspection', label: 'Create New Inspection', icon: 'plus', route: '/inspections-edit-property' });
    } else if (ctx.inspectionTabOrder == 0) {
        inspectionLinks.push({ id: 'create-inspection', label: 'Create New Inspection', icon: 'plus', route: '/inspections-order-parties' });
    }
    inspectionLinks.push({ id: 'view-inspections', label: 'View Inspections', icon: 'list', route: '/inspections-list' });
    menu.push({
        id: 'inspections', label: 'Inspections', icon: 'clipboard', flyoutTitle: 'Inspections / Orders', landing: 'view-inspections',
        sections: [{ links: inspectionLinks }]
    });

    if (ctx.isAdministrator) {
        menu.push({ id: 'calendar', label: 'Calendar', icon: 'calendar', route: '/calendar' });
    }

    menu.push({
        id: 'clients', label: 'Clients', icon: 'users', flyoutTitle: 'Clients', landing: 'list-clients',
        // One list, as the mockup's Clients menu (no separate Agencies heading).
        sections: [
            {
                title: 'Clients', landing: 'list-clients',
                links: [
                    { id: 'create-client', label: 'Create New Client', icon: 'plus', route: '/clients/add' },
                    { id: 'list-clients', label: 'List Clients', icon: 'list', route: '/clients/list' },
                    { id: 'create-agency', label: 'Create New Agency', icon: 'plus', route: '/agencies/add' },
                    { id: 'list-agencies', label: 'List Agencies', icon: 'building', route: '/agencies/list' }
                ]
            }
        ]
    });

    // Reports: names and descriptions follow the mockup's Report Library (2026-10-03); ids are
    // unchanged so saved Quick Link pins keep working. Agent Activity stays administrators only.
    const managementReports: NavLink[] = [
        { id: 'inspections-scheduled', label: 'Inspections Scheduled', icon: 'calendar', route: '/accounting-report-inspections-scheduled',
          description: 'Compare scheduled, performed, incomplete, and cancelled inspections by type and county.' },
        // Residential Inspections and Charges hidden (2026-10-08, asked by the client); the twin-ci
        // report management_report_residential_inspections still exists.
        { id: 'inspector-performance', label: 'Inspector Performance Analysis', icon: 'trend', route: '/management-report-inspectors-sales',
          description: 'Compare inspections, completions, revenue, and average production by inspector.' }
    ];
    if (ctx.isAdministrator) {
        managementReports.push({ id: 'agent-activity', label: 'Agent Activity', icon: 'agent', route: '/agent-report-sales',
          description: 'Compare inspections, completions, re-inspections, revenue, and productivity by agent.' });
    }
    // Chemical Usage and Cancellations are twin-ci administrator pages only.
    if (ctx.isAdministrator) {
        managementReports.push(
            { id: 'chemical-usage', label: 'Chemical Usage', icon: 'flask', route: '/management-report-chemical-usage',
              description: 'Review chemical applications, EPA registration, amounts, and county totals.' },
            { id: 'cancellation-report', label: 'Cancellations', icon: 'close', route: '/management-cancellation-report',
              description: 'Review cancelled inspection orders, responsible users, and recorded reasons.' }
        );
    }
    managementReports.push(
        { id: 'discount-report', label: 'Discounts', icon: 'tag', route: '/discount-report',
          description: 'Review discount amounts by type, inspector, property, and original fees.' },
        { id: 'expiring-reports', label: 'Expiring Reports', icon: 'file', route: '/expiring-reports-with-bids',
          description: 'Find inspection reports approaching expiration with outstanding bids.' },
        { id: 'wdo-activity', label: 'WDO Activity Report', icon: 'clipboard', route: '/activity-report',
          description: 'Generate activity reports ready to be sent to the Board.' }
    );

    // Marketing reports were twin-ci business/* pages, administrators only.
    const marketingSections: NavSection[] = ctx.isAdministrator ? [{
        title: 'Marketing',
        links: [
            { id: 'agents-not-using-us', label: 'Zero Completion Agents', icon: 'agent', route: '/report-agents-unused-work',
              description: 'Find agents who have ordered inspections with us but have never scheduled the repairs.' },
            { id: 'new-agents', label: 'New Agents', icon: 'agent', route: '/new-clients',
              description: 'Review recently added agents and compare their latest inspection orders.' },
            { id: 'jobs-with-bids', label: 'Unscheduled Jobs with Bids', icon: 'file', route: '/report-job-with-bids-non-scheduled',
              description: 'Find completed inspections where repair bids were provided, but the work has not been booked on the schedule.' }
        ]
    }] : [];

    // Reports opens the Report Library (route). Its sections are still the list of reports, used by
    // the library itself, Quick Links and breadcrumbs.
    menu.push({
        id: 'reports', label: 'Reports', icon: 'reports', flyoutTitle: 'Reports', route: '/reports',
        sections: [
            ...marketingSections,
            {
                title: 'Accounting',
                links: [
                    { id: 'payments-received', label: 'Payments Received', icon: 'payment', route: '/accounting-report-payment',
                      description: 'Review received payments by property, inspector, transaction description, and work type.' },
                    { id: 'payroll-details-payment', label: 'Payroll Details (Payment)', icon: 'file', route: '/accounting-report-payroll-payment',
                      description: 'Review inspector compensation based on payments received.' },
                    { id: 'payroll-details-charges', label: 'Payroll Details (Charges)', icon: 'file', route: '/accounting-report-payroll-charge',
                      description: 'Review inspector compensation based on charges billed.' },
                    { id: 'charges-by-inspector', label: 'Charges by Inspector', icon: 'reports', route: '/accounting-report-payroll-summary',
                      description: 'Review inspection, completion, and processing charges grouped by inspector.' },
                    { id: 'revenue-by-type', label: 'Revenue by Type', icon: 'dollar', route: '/accounting-report-revenue',
                      description: 'Compare completed work, inspection fees, card charges, and discounts by revenue type.' },
                    { id: 'property-aging', label: 'Property Aging', icon: 'home-clock', route: '/accounting-report-property-aging',
                      description: 'Accounts receivable report. Track outstanding property balances across current, 30, 60, and 90-day aging periods.' },
                    // Crew Commission hidden (2026-10-06): the twin-ci page has never had queries
                    // (always "No Result Found") and the mockup has no design for it.
                    { id: 'job-costing', label: 'Job Costing', icon: 'file', route: '/accounting-report-job-costing',
                      description: 'Compare labor, materials, subcontractor costs, overhead, charges, and profitability by job.' },
                    { id: 'estimated-vs-actual', label: 'Estimated vs Actual', icon: 'trend', route: '/accounting-report-estimated-vs-actual',
                      description: 'Compare planned labor hours and material costs with actual completed work by inspector.' }
                ]
            },
            { title: 'Management', links: managementReports }
        ]
    });

    menu.push({
        id: 'settings', label: 'Settings', icon: 'settings', route: '/settings', flyoutTitle: 'Settings',
        sections: buildSettingsSections(ctx, ci)
    });

    return menu;
}

// The line under each settings page title (mockup settings data-description, 2026-10-21; split
// where we keep separate pages for what the mockup puts on one). By link id.
const SETTINGS_DESCRIPTIONS: { [id: string]: string } = {
    'settings-company-profile': 'Manage company details, official signature, favicon, and sidebar, login, report, and Job Card logos.',
    'branches': 'Manage branch details, ZIP-code coverage, and master crew hourly rates.',
    'system-settings': 'Configure application details, support contacts, time zone, and date defaults.',
    'settings-dashboard-settings': 'Set the dashboard messages and preferences.',
    'settings-lockout': 'Set the system and findings lockout date, PIN, and messages.',
    'users-list': 'Find and edit users, and manage widget groups.',
    'users-levels': 'Manage user levels and what each one can view and manage.',
    'settings-inspection-general': 'Set fee dates, whole dollar rounding, workflow defaults, and inspection tab order.',
    'calendar-settings': 'Calendar and appointment configuration.',
    'settings-time-calculator': 'Estimate inspection duration from property characteristics.',
    'findings-settings': 'Set how finding types are numbered.',
    'office-fees': 'Manage inspection fees by branch, square footage, foundation type, and fee group.',
    'settings-pricing': 'Configure treatment costs, markups, subcontractors, and overhead.',
    'findings-report-settings': 'Configure findings report settings.',
    'work-contract-settings': 'Configure work authorization contract settings.',
    'billing-report-settings': 'Configure billing statement settings.',
    'document-library': 'Organize reusable documents.',
    'auto-emails': 'Manage the default email container, appointment confirmation, and reusable replies.',
    'smtp-settings': 'Configure outgoing email delivery and system-generated email defaults.',
    'settings-agent-outreach': 'Define new-agent, VIP, and agent dropoff criteria.',
    'settings-weekly-report': 'Set weekly report delivery day, time, and dollar-value criteria.',
    'codes': 'Organize inspection codes, descriptions, WDO sections, and revenue types.',
    'revenue-types': 'Manage revenue categories and their Codebook associations.',
    'chemicals': 'Maintain chemical codes, units, EPA registrations, and descriptions.',
    'leads': 'Manage client acquisition sources and notes.',
    'database-export': 'Export data as CSV, or update and add records from CSV (checked 1000 rows at a time; changes cannot be undone).',
    'settings-e-signatures': 'Manage the Adobe Sign and DocuSign connections.',
    'change-password': 'Update your own profile picture, password, display, and accessibility preferences.',
};

// ---- Settings (mockup settings: groups and names) ----
//
// Settings is one page (/settings, the Settings workspace) whose left list holds these groups.
// Who sees which link follows twin-ci's staff menu (templates/staff/leftmenu.php verify_me
// labels); administrators see everything. Pages still in twin-ci open there (href). Pages whose
// design is not built yet open a blank page in the workspace (/settings/<key>, `planned`); they
// are parts of System Settings, so they follow its permission.
// Link ids are the ones the old Settings fly-out used, so pinned Quick Links keep working.
function buildSettingsSections(ctx: NavContext, ci: (slug: string) => string): NavSection[] {
    const admin = ctx.isAdministrator;
    const can = (label: string) => admin || (ctx.permissions || []).indexOf(label) !== -1;
    const config = (label?: string) => admin || (can('Configuration') && (!label || can(label)));
    const system = config('Configuration - Configure System Settings');
    const planned = (id: string, label: string, scope?: string): NavLink =>
        ({ id: 'settings-' + id, label, icon: 'settings', route: '/settings/' + id, planned: true, scope });
    // A System Settings part whose page is built (same URL as while it was planned).
    const systemPage = (id: string, label: string): NavLink =>
        ({ id: 'settings-' + id, label, icon: 'settings', route: '/settings/' + id });
    const pick = (links: NavLink[], keep: boolean[]) => links.filter((l, i) => keep[i]);

    // Groups and order of the mockup settings menu (2026-10-21: Company & System first). Every page we have is kept, also
    // those the mockup folds into another page (Data Export / Import, Adobe Sign / DocuSign,
    // Auto Emails / Canned Replies, Company Profile / Branding, the System Settings parts);
    // names follow the mockup where a page matches one of its items.
    const sections: NavSection[] = [
        // Mockup "Company Profile & Branding" holds the company, official signature and logos.
        { title: 'Company & System', links: pick([
            systemPage('company-profile', 'Company Profile & Branding'),
            { id: 'branches', label: 'Territories & Branches', icon: 'map-pin', route: '/branches' },
            { id: 'system-settings', label: 'System Settings', icon: 'settings', route: '/settings/system' },
            systemPage('dashboard-settings', 'Dashboard Settings'),
            systemPage('lockout', 'Lockout Settings')
        ], [system, config('Configuration - Manage Branches'), system, system, system]) },
        { title: 'Users & Permissions', links: pick([
            { id: 'users-list', label: 'Users', icon: 'list', route: '/users' },
            { id: 'users-levels', label: 'Roles & Permissions', icon: 'account', route: '/roles-permissions' }
            // Widget Groups (was Share Widgets) is a section of Users (mockup); /share-widgets opens Users.
        ], [admin, admin]) },  // administrators only, as before
        // The mockup's "General Settings" (tab order, whole dollars, fee charge date, report
        // defaults) comes first here, as four pages; then its Calendar and Time Calculator.
        { title: 'Inspections & Scheduling', links: pick([
            systemPage('inspection-general', 'General Settings'),
            { id: 'calendar-settings', label: 'Calendar Appointment Settings', icon: 'calendar', route: '/calendar-settings' },
            systemPage('time-calculator', 'Inspection Time Calculator'),
            { id: 'findings-settings', label: 'Findings Settings', icon: 'clipboard', route: '/findings-settings' }
        ], [system, config(), system, admin]) },
        { title: 'Pricing & Billing', links: pick([
            { id: 'office-fees', label: 'Inspection Fees', icon: 'dollar', route: '/office-fees' },
            systemPage('pricing', 'Bid Pricing & Markups')
        ], [admin, system]) },
        { title: 'Reports & Documents', links: pick([
            { id: 'findings-report-settings', label: 'Findings Report (WDO)', icon: 'file', route: '/findings-report-settings' },
            { id: 'work-contract-settings', label: 'Work Authorization Contract', icon: 'file', route: '/report-settings' },
            { id: 'billing-report-settings', label: 'Billing Statement', icon: 'payment', route: '/billing-report-settings' },
            { id: 'document-library', label: 'Document Library', icon: 'file', route: '/document-library' }
        ], [admin, admin, admin, config()]) },
        // Mockup: the email header / footer, appointment confirmations and canned replies on one
        // page; SMTP on its own (2026-10-21).
        { title: 'Communications', links: pick([
            { id: 'auto-emails', label: 'Appointment Emails & Canned Replies', icon: 'mail', route: '/auto-emails' },
            { id: 'smtp-settings', label: 'SMTP Settings', icon: 'mail', route: '/smtp-settings' }
        ], [config('Configuration - Edit Automated Emails'), config('Configuration - Edit Automated Emails')]) },
        { title: 'Business Development & Marketing', links: pick([
            systemPage('agent-outreach', 'Agent Outreach'),
            systemPage('weekly-report', 'Inspector Weekly Job Report')
            // Sales Quotas / Quota Assignment hidden (2026-10-09, agreed); the twin-ci pages still exist.
        ], [system, system]) },
        { title: 'Data', links: pick([
            { id: 'codes', label: 'Codebook', icon: 'list', route: '/codes-list' },
            { id: 'revenue-types', label: 'Revenue Types', icon: 'dollar', route: '/revenue-types' },
            { id: 'chemicals', label: 'Chemicals', icon: 'flask', route: '/chemicals' },
            { id: 'leads', label: 'Lead Sources', icon: 'agent', route: '/lead-sources' },
            // Data level export / import (2026-10-19; replaces twin-ci's raw table dump). Same ids, so
            // pinned Quick Links keep working.
            { id: 'database-export', label: 'Database Import & Export', icon: 'database', route: '/data-exchange' }
        ], [config('Configuration - Manage Codes'), config(), config('Configuration - Manage Chemicals'), config(), admin]) },
        { title: 'Integrations', links: pick([
            systemPage('e-signatures', 'E-Signature Providers')
        ], [system]) },
        { title: 'My Account', links: [
            // Same id as the old Change Password link, so pinned Quick Links keep working.
            { id: 'change-password', label: 'Profile, Password & Preferences', icon: 'key', route: '/my-account', scope: 'personal' }
        ] }
    ];
    sections.forEach(section => section.links.forEach(link => link.description = SETTINGS_DESCRIPTIONS[link.id] || link.description));
    return sections.filter(s => s.links.length);
}
